<template>
  <div class="home-page">
    <section class="home-hero">
      <div class="home-shell home-hero__grid">

        <!-- Left -->
        <div class="home-hero__left">
          <div class="home-hero__eyebrow">
            <span></span>
            <p>{{ t('home.heroBadge') }}</p>
          </div>

          <h1 class="home-hero__title">
            <span>{{ t('home.heroTitleMainLine1') }}</span>
            <span>{{ t('home.heroTitleMainLine2') }}</span>
            <span>{{ t('home.heroTitleMainLine3') }}</span>
            <em class="home-hero__accent">
              <span>{{ t('home.heroTitleAccentPart1') }}</span>
              <span>{{ t('home.heroTitleAccentPart2') }}</span>
            </em>
          </h1>

          <p class="home-hero__subline">{{ t('home.heroSubtitle') }}</p>

          <p class="home-hero__desc">{{ t('home.heroLead') }}</p>

          <div class="home-hero__actions">
            <router-link
              to="/contact"
              class="home-hero__btn home-hero__btn--primary"
            >
              {{ t('home.startNow') }}
              <i class="fas fa-arrow-right"></i>
            </router-link>
            <router-link to="/about" class="home-hero__btn home-hero__btn--secondary">
              {{ t('home.viewStrategy') }}
            </router-link>
          </div>

          <div class="home-hero__stats">
            <div v-for="item in heroStats" :key="item.key" class="home-hero__stat">
              <strong>{{ item.value }}</strong>
              <span>{{ t(item.key) }}</span>
            </div>
          </div>
        </div>

        <!-- Right: dashboard panel -->
        <div class="home-hero__right">
          <div class="home-hero__panel">

            <!-- Panel top bar -->
            <div class="home-hero__panel-bar">
              <span>{{ t('home.heroPanelDashboard') }}</span>
              <span class="home-hero__panel-live">
                <i></i> {{ t('home.live') }}
              </span>
            </div>

            <!-- Portfolio header -->
            <div class="home-hero__panel-header">
              <div>
                <p class="home-hero__panel-name">SHC-M Pro Portfolio</p>
                <small>{{ t('home.heroStrategy') }}</small>
              </div>
              <div class="home-hero__panel-profit">
                <strong v-if="activeHeroTab === 'vnindex'">{{ vnIndexMeta.change >= 0 ? '+' : '' }}{{ vnIndexMeta.change.toFixed(2) }}</strong>
                <strong v-else>{{ shcMeta.net >= 0 ? '+' : '' }}{{ shcMeta.net.toFixed(2) }}</strong>
                <small v-if="activeHeroTab === 'vnindex'">{{ t('home.vnIndexProfit') }} 08/05/2026</small>
                <small v-else>{{ t('home.shcProfit') }} · SHC-M Pro Portfolio</small>
              </div>
            </div>

            <!-- Mini charts -->
            <div class="home-hero__panel-chart" ref="chartContainer">
              <div class="home-hero__chart-block home-hero__chart-block--vnindex">
                <div class="home-hero__chart-block-head">
                  <strong>VN Index</strong>
                  <span>31/03/2022 → 08/05/2026</span>
                </div>
                <svg
                  :viewBox="`0 0 ${vnIndexChart.W} ${vnIndexChart.H}`"
                  preserveAspectRatio="none"
                  style="display:block; width:100%; cursor:crosshair;"
                  @mousemove.stop="onVnIndexMove"
                  @mouseleave="onVnIndexLeave"
                >
                  <defs>
                    <linearGradient id="vnIndexGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stop-color="#c8a246" stop-opacity="0.24"/>
                      <stop offset="100%" stop-color="#c8a246" stop-opacity="0.02"/>
                    </linearGradient>
                  </defs>
                  <line
                    x1="0" :y1="vnIndexChart.zeroY"
                    :x2="vnIndexChart.W" :y2="vnIndexChart.zeroY"
                    stroke="rgba(0,0,0,.08)" stroke-width="1" stroke-dasharray="4 3"
                  />
                  <path :d="vnIndexChart.fillD" fill="url(#vnIndexGrad)" stroke="none"/>
                  <polyline
                    :points="vnIndexChart.linePts"
                    fill="none"
                    stroke="#b8890b"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <circle
                    v-if="vnIndexChart.ptCoords.length"
                    :cx="vnIndexChart.ptCoords[vnIndexChart.ptCoords.length - 1].x"
                    :cy="vnIndexChart.ptCoords[vnIndexChart.ptCoords.length - 1].y"
                    r="3"
                    fill="#b8890b"
                  />

                  <g v-if="vnIndexTooltip">
                    <line
                      :x1="vnIndexTooltip.svgX" y1="0"
                      :x2="vnIndexTooltip.svgX" :y2="vnIndexChart.H"
                      stroke="rgba(0,0,0,.14)" stroke-width="1" stroke-dasharray="3 2"
                    />
                    <circle
                      :cx="vnIndexTooltip.svgX" :cy="vnIndexTooltip.svgY"
                      r="4"
                      fill="#b8890b"
                      stroke="#fff" stroke-width="1.5"
                    />
                  </g>
                </svg>

                <div
                  v-if="vnIndexTooltip"
                  class="hero-chart-tip hero-chart-tip--vnindex"
                  :style="vnIndexTooltipStyle"
                >
                  <span class="hero-chart-tip__pair">
                    VNINDEX
                    <em class="buy">{{ vnIndexTooltip.date }}</em>
                  </span>
                  <span class="hero-chart-tip__profit">{{ vnIndexTooltip.value.toFixed(2) }}</span>
                  <span class="hero-chart-tip__cum">
                    {{ vnIndexTooltip.delta >= 0 ? '+' : '' }}{{ vnIndexTooltip.delta.toFixed(2) }} từ đầu kỳ
                  </span>
                </div>
              </div>

              <div class="home-hero__chart-block">
                <div class="home-hero__chart-block-head">
                  <strong>SHC-M Pro Portfolio</strong>
                  <span>{{ t('home.shcCumulativePL') }}</span>
                </div>
                <svg
                  :viewBox="`0 0 ${heroChart.W} ${heroChart.H}`"
                  preserveAspectRatio="none"
                  style="cursor:crosshair; display:block; width:100%;"
                  @mousemove.stop="onHeroChartMove"
                  @mouseleave="onHeroChartLeave"
                >
                  <defs>
                    <linearGradient id="heroGradUp" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stop-color="#2f8c56" stop-opacity="0.22"/>
                      <stop offset="100%" stop-color="#2f8c56" stop-opacity="0"/>
                    </linearGradient>
                    <linearGradient id="heroGradDown" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stop-color="#c0504d" stop-opacity="0.15"/>
                      <stop offset="100%" stop-color="#c0504d" stop-opacity="0"/>
                    </linearGradient>
                  </defs>

                  <line x1="0" :y1="heroChart.zeroY" :x2="heroChart.W" :y2="heroChart.zeroY"
                    stroke="rgba(0,0,0,.08)" stroke-width="1" stroke-dasharray="4 3"/>
                  <path :d="heroChart.fillD"
                    :fill="heroChart.trend === 'up' ? 'url(#heroGradUp)' : 'url(#heroGradDown)'"
                    stroke="none"/>
                  <polyline :points="heroChart.linePts"
                    fill="none"
                    :stroke="heroChart.trend === 'up' ? '#2f8c56' : '#c0504d'"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"/>
                  <circle
                    v-if="heroChart.ptCoords.length"
                    :cx="heroChart.ptCoords[heroChart.ptCoords.length - 1].x"
                    :cy="heroChart.ptCoords[heroChart.ptCoords.length - 1].y"
                    r="3"
                    :fill="heroChart.trend === 'up' ? '#2f8c56' : '#c0504d'"
                  />

                  <g v-if="heroTooltip">
                    <line
                      :x1="heroTooltip.svgX" y1="0"
                      :x2="heroTooltip.svgX" :y2="heroChart.H"
                      stroke="rgba(0,0,0,.15)" stroke-width="1" stroke-dasharray="3 2"
                    />
                    <circle
                      :cx="heroTooltip.svgX" :cy="heroTooltip.svgY"
                      r="4"
                      :fill="heroTooltip.profit >= 0 ? '#2f8c56' : '#c0504d'"
                      stroke="#fff" stroke-width="1.5"
                    />
                  </g>
                </svg>

                <div
                  v-if="heroTooltip"
                  class="hero-chart-tip"
                  :class="heroTooltip.profit >= 0 ? 'hero-chart-tip--win' : 'hero-chart-tip--loss'"
                  :style="heroTooltipStyle"
                >
                  <span class="hero-chart-tip__pair">
                    {{ heroTooltip.pair }}
                    <em :class="heroTooltip.type === 'BUY' ? 'buy' : 'sell'">{{ heroTooltip.type }}</em>
                  </span>
                  <span class="hero-chart-tip__profit">{{ heroTooltip.profit >= 0 ? '+' : '' }}{{ heroTooltip.profit.toFixed(2) }}</span>
                  <span class="hero-chart-tip__cum">Tổng: {{ heroTooltip.cum.toFixed(2) }}</span>
                </div>
              </div>
            </div>

            <div class="home-hero__panel-tabs">
              <button :class="['home-hero__panel-tab', activeHeroTab === 'vnindex' ? 'is-active' : '']" @click="activeHeroTab = 'vnindex'">VN Index</button>
              <button :class="['home-hero__panel-tab', activeHeroTab === 'shc' ? 'is-active' : '']" @click="activeHeroTab = 'shc'">SHC-M Pro</button>
            </div>

            <div class="home-hero__panel-meta" v-if="activeHeroTab === 'vnindex'">
              <div><small>{{ t('home.vnIndexProfit') }}</small><span>{{ vnIndexMeta.latest.toFixed(2) }}</span></div>
              <div><small>{{ t('home.vnIndexChange') }}</small><span>{{ vnIndexMeta.change >= 0 ? '+' : '' }}{{ vnIndexMeta.change.toFixed(2) }}</span></div>
              <div><small>{{ t('home.vnIndexReturn') }}</small><span>{{ vnIndexMeta.returnPct >= 0 ? '+' : '' }}{{ vnIndexMeta.returnPct.toFixed(2) }}%</span></div>
              <div><small>{{ t('home.live') }}</small><span>08/05/2026</span></div>
            </div>
            <div class="home-hero__panel-meta" v-else>
              <div><small>{{ t('home.shcDeals') }}</small><span>{{ shcMeta.deals }}</span></div>
              <div><small>{{ t('home.shcWL') }}</small><span>{{ shcMeta.win }} / {{ shcMeta.loss }}</span></div>
              <div><small>{{ t('home.shcWinRate') }}</small><span>{{ shcMeta.winRate.toFixed(0) }}%</span></div>
              <div><small>{{ t('home.shcProfit') }}</small><span>{{ shcMeta.net >= 0 ? '+' : '' }}{{ shcMeta.net.toFixed(2) }}</span></div>
            </div>

            <div :class="['home-hero__panel-rows', activeHeroTab === 'vnindex' ? 'is-positive' : (shcMeta.net >= 0 ? 'is-positive' : 'is-negative')]">
              <div
                v-for="row in (activeHeroTab === 'vnindex' ? vnIndexRows : shcRows)"
                :key="row.date + row.label + (row.pair || '')"
                :class="['home-hero__panel-row', row.positive ? 'is-positive' : 'is-negative']"
              >
                <div class="home-hero__panel-row-left">
                  <span class="home-hero__panel-row-pair">{{ activeHeroTab === 'vnindex' ? row.date : row.pair }}</span>
                  <span
                    v-if="activeHeroTab !== 'vnindex'"
                    :class="['home-hero__panel-row-badge', row.positive ? 'home-hero__panel-row-badge--buy' : 'home-hero__panel-row-badge--sell']"
                  >{{ row.label }}</span>
                </div>
                <div class="home-hero__panel-row-bar"><i :style="{ width: row.widthPct + '%' }"></i></div>
                <div class="home-hero__panel-row-right">
                  <span :class="['home-hero__panel-row-profit', row.positive ? 'positive' : 'negative']">{{ activeHeroTab === 'vnindex' ? row.value.toFixed(2) : ((row.perf >= 0 ? '+' : '') + row.perf.toFixed(2)) }}</span>
                  <span class="home-hero__panel-row-price">{{ activeHeroTab === 'vnindex' ? ((row.perf >= 0 ? '+' : '') + row.perf.toFixed(2) + '%') : row.value.toFixed(5) }}</span>
                </div>
              </div>
            </div>

          </div>
        </div>

      </div>
    </section>

    <section class="home-pathways">
      <div class="home-shell">
        <div class="home-pathways__header">
          <p class="home-pathways__eyebrow">{{ t('home.pathwaysEyebrow') }}</p>
          <h2 class="home-pathways__title">{{ t('home.pathwaysTitle') }}</h2>
        </div>

        <div class="home-pathways__cards">
          <router-link to="/invest" class="home-pathways__card home-pathways__card--invest">
            <span class="home-pathways__icon"><i class="fas fa-coins"></i></span>
            <div class="home-pathways__card-body">
              <span class="home-pathways__card-kicker">{{ t('home.pathwaysInvestKicker') }}</span>
              <strong class="home-pathways__card-title">{{ t('home.pathwaysInvestTitle') }}</strong>
            </div>
            <span class="home-pathways__card-cta">{{ t('home.pathwaysCta') }} <i class="fas fa-arrow-right"></i></span>
          </router-link>

          <router-link to="/fund" class="home-pathways__card home-pathways__card--fund">
            <span class="home-pathways__icon"><i class="fas fa-file-pdf"></i></span>
            <div class="home-pathways__card-body">
              <span class="home-pathways__card-kicker">{{ t('home.pathwaysFundKicker') }}</span>
              <strong class="home-pathways__card-title">{{ t('home.pathwaysFundTitle') }}</strong>
            </div>
            <span class="home-pathways__card-cta">{{ t('home.pathwaysCta') }} <i class="fas fa-arrow-right"></i></span>
          </router-link>
        </div>
      </div>
    </section>

    <section class="home-machine">
      <div class="home-shell">
        <div
          class="home-machine__header"
          role="link"
          tabindex="0"
          @click="$router.push('/shc-machine')"
          @keydown.enter="$router.push('/shc-machine')"
          @keydown.space.prevent="$router.push('/shc-machine')"
        >
          <div class="home-machine__header-text">
            <div class="home-machine__badge">
              <span></span>
              <p>{{ t('home.SignalLabel') }}</p>
            </div>
            <h2 class="home-machine__title">{{ t('home.SignalTitle') }}</h2>
            <p class="home-machine__subtitle">{{ t('home.SignalSubtitle') }}</p>
          </div>
        </div>

        <div class="home-machine__grid">
          <div class="home-machine__left">
            <div v-for="(step, index) in machineSteps" :key="index" class="home-machine__step">
              <div class="home-machine__step-number">{{ String(index + 1).padStart(2, '0') }}</div>
              <div class="home-machine__step-content">
                <h3>{{ t(step.titleKey) }}</h3>
                <p>{{ t(step.descKey) }}</p>
                <div class="home-machine__tags">
                  <span v-for="tag in step.tags" :key="tag" class="home-machine__tag">{{ tag }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="home-machine__right">
            <div class="home-machine__panel">
              <!-- Header + stats tổng -->
              <div class="home-machine__panel-header">
                <div class="hm-header__left">
                  <span class="hm-header__title">{{ t('home.autoTradeLog') }}</span>
                  <span class="hm-header__sep">·</span>
                  <span class="hm-header__today">{{ t('home.oneDay') }}</span>
                </div>
                <div class="hm-header__right">
                  <span class="hm-header__live-dot"></span>
                  <span class="hm-header__live-text">{{ t('home.live') }}</span>
                </div>
              </div>

              <!-- Cumulative profit sparkline -->
              <div class="hm-sparkline">
                <svg
                  :viewBox="`0 0 ${tradeChart.sparkW} 56`"
                  preserveAspectRatio="none"
                  class="hm-sparkline__svg"
                  @mousemove="($event) => {
                    const rect = $event.currentTarget.getBoundingClientRect();
                    const scaleX = tradeChart.sparkW / rect.width;
                    const mouseX = ($event.clientX - rect.left) * scaleX;
                    const point = tradeChart.sparkPoints.reduce((best, p) => Math.abs(p.x - mouseX) < Math.abs(best.x - mouseX) ? p : best, tradeChart.sparkPoints[0]);
                    onSparklineMove($event, point);
                  }"
                  @mouseleave="onSparklineLeave"
                >
                  <defs>
                    <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stop-color="#2f8c56" stop-opacity="0.15"/>
                      <stop offset="100%" stop-color="#2f8c56" stop-opacity="0"/>
                    </linearGradient>
                  </defs>
                  <!-- Zero baseline -->
                  <line x1="0" :y1="tradeChart.sparkZeroY" :x2="tradeChart.sparkW" :y2="tradeChart.sparkZeroY"
                    stroke="#e4ddd0" stroke-width="1"/>
                  <path :d="tradeChart.sparkFill" fill="url(#sparkGrad)"/>
                  <polyline :points="tradeChart.sparkLine" fill="none" stroke="#2f8c56" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <div class="hm-sparkline__labels">
                  <span v-for="h in tradeChart.timeGrid" :key="h.label" v-show="h.isHour"
                    :style="{ left: h.pct + '%' }">{{ h.label }}</span>
                </div>
                <div v-if="sparkTooltip" class="hp-tooltip hp-tooltip--floating" :style="sparkTooltipStyle">
                  <span class="hp-tooltip__team" style="color:#2f8c56">{{ sparkTooltip.pair }}</span>
                  <span class="hp-tooltip__month">{{ sparkTooltip.time }}</span>
                  <span class="hp-tooltip__val" style="color:#2f8c56">+{{ sparkTooltip.profit.toFixed(2) }}</span>
                  <span class="hp-tooltip__month">Tổng: {{ sparkTooltip.cum.toFixed(2) }}</span>
                </div>
              </div>

              <!-- Column headers -->
              <div class="hm-thead">
                <span>{{ $t('home.oneDay').replace('Hôm', 'Giờ') || 'Giờ' }}</span>
                <span>Pair</span>
                <span>{{ $t('home.entryPrice') }}</span>
                <span>P&amp;L</span>
              </div>

              <!-- Timeline: mỗi lệnh 1 row -->
              <div class="hm-timeline">
                <div
                  v-for="(trade, idx) in tradeChart.sorted"
                  :key="idx"
                  :class="['hm-row', trade.profit > 0 ? 'hm-row--win' : 'hm-row--loss']"
                >
                  <!-- Giờ -->
                  <span class="hm-row__time">{{ trade.time }}</span>

                  <!-- Pair + badge -->
                  <div class="hm-row__pair-wrap">
                    <span class="hm-row__pair">{{ trade.pair }}</span>
                    <span :class="['hm-row__badge', trade.type === 'BUY' ? 'hm-row__badge--buy' : 'hm-row__badge--sell']">
                      {{ trade.type }}
                    </span>
                  </div>

                  <!-- Giá vào + lot -->
                  <div class="hm-row__meta">
                    <span class="hm-row__price">{{ trade.price }}</span>
                    <span class="hm-row__lot">{{ trade.lot }} lot</span>
                  </div>

                  <!-- P&L: bar + số -->
                  <div class="hm-row__pnl">
                    <div class="hm-row__bar-wrap">
                      <div
                        class="hm-row__bar"
                        :style="{
                          width: Math.abs(trade.profit / tradeChart.maxAbsProfit * 100) + '%',
                          background: trade.profit > 0 ? 'linear-gradient(90deg,#2f8c56,#3daa6a)' : 'linear-gradient(90deg,#c0504d,#e06460)'
                        }"
                      ></div>
                    </div>
                    <span :class="['hm-row__profit', trade.profit > 0 ? 'hm-row__profit--win' : 'hm-row__profit--loss']">
                      {{ trade.profit > 0 ? '+' : '' }}{{ trade.profit.toFixed(2) }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Footer summary -->
              <div class="hm-footer">
                <span class="hm-footer__item">{{ tradeChart.total }} {{ t('home.commander') }}</span>
                <span class="hm-footer__sep">·</span>
                <span class="hm-footer__item hm-footer__item--profit">+{{ tradeChart.totalProfit }}</span>
                <span class="hm-footer__sep">·</span>
                <span class="hm-footer__item">Win {{ tradeChart.winRate }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="home-strategy">
      <div class="home-shell">
        <div
          class="home-strategy__header"
          role="link"
          tabindex="0"
          @click="$router.push('/strategies')"
          @keydown.enter="$router.push('/strategies')"
          @keydown.space.prevent="$router.push('/strategies')"
        >
          <div class="home-strategy__header-left">
            <div class="home-strategy__badge">
              <span></span>
              <p>{{ t('home.strategyLabel') }}</p>
            </div>
            <h2 class="home-strategy__title">{{ t('home.strategyTitle') }}</h2>
          </div>
          <p class="home-strategy__desc">{{ t('home.strategyDesc') }}</p>
        </div>

        <div class="home-strategy__grid">
          <div
            v-for="(s, idx) in strategies"
            :key="idx"
            :class="['home-strategy__card', s.featured ? 'home-strategy__card--featured' : '']"
          >
            <!-- Top accent bar -->
            <div class="home-strategy__card-bar"></div>

            <div class="home-strategy__card-inner">
              <!-- Eyebrow + badge -->
              <div class="home-strategy__card-eyebrow">
                <span>{{ t(s.labelKey) }}</span>
                <em v-if="s.featured">{{ t('home.strategyPopular') }}</em>
              </div>

              <!-- Title -->
              <h3 class="home-strategy__card-title">{{ t(s.titleKey) }}</h3>

              <!-- Desc -->
              <p class="home-strategy__card-desc">{{ t(s.descKey) }}</p>

              <!-- Return — hero number -->
              <div class="home-strategy__card-return">
                <span class="home-strategy__card-return-val">{{ s.returnRange }}</span>
                <span class="home-strategy__card-return-label">{{ t('home.metricReturn') }}</span>
              </div>

              <!-- Metrics row -->
              <div class="home-strategy__metrics">
                <div class="home-strategy__metric">
                  <small>{{ t('home.metricDrawdown') }}</small>
                  <strong class="negative">{{ s.drawdown }}</strong>
                </div>
                <div class="home-strategy__metric">
                  <small>{{ t('home.metricWinRate') }}</small>
                  <strong>{{ s.winRate }}</strong>
                </div>
                <div class="home-strategy__metric">
                  <small>{{ t('home.metricSharpe') }}</small>
                  <strong>{{ s.sharpe }}</strong>
                </div>
              </div>

              <!-- Risk -->
              <div class="home-strategy__risk">
                <div class="home-strategy__risk-dots">
                  <i
                    v-for="n in 5"
                    :key="n"
                    :class="['home-strategy__risk-dot', n <= s.riskLevel ? 'active' : '']"
                  ></i>
                </div>
                <span>{{ t(s.riskLabelKey) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="home-perf">
      <div class="home-shell">
        <div
          class="home-perf__header"
          role="link"
          tabindex="0"
          @click="$router.push('/performance')"
          @keydown.enter="$router.push('/performance')"
          @keydown.space.prevent="$router.push('/performance')"
        >
          <div class="home-perf__header-left">
            <div class="home-perf__badge">
              <span></span>
              <p>{{ t('home.perfLabel') }}</p>
            </div>
            <h2 class="home-perf__title">{{ t('home.perfTitle') }}</h2>
            <p class="home-perf__subtitle">{{ t('home.perfSubtitle') }}</p>
          </div>
        </div>

        <div class="home-perf__grid">

          <!-- Left: table -->
          <div class="home-perf__left">

            <div class="home-perf__table">
              <div class="home-perf__table-head">
                <span>{{ t('home.perfMonth') }}</span>
                <span>SHC Team V1</span>
                <span>SHC Team V3</span>
                <span>{{ t('home.perfMarket') }}</span>
              </div>
              <div
                v-for="(row, i) in perfRows"
                :key="i"
                :class="['home-perf__table-row', row.highlight ? 'home-perf__table-row--highlight' : '']"
              >
                <span>{{ row.month }}</span>
                <span class="positive">{{ row.v1 }}</span>
                <span class="positive">{{ row.v3 }}</span>
                <span class="negative">{{ row.market }}</span>
              </div>
            </div>
          </div>

          <!-- Right: chart + stats -->
          <div class="home-perf__right">
            <div class="home-perf__chart-header">
              <p class="home-perf__chart-label">{{ t('home.perfChartLabel') }}</p>
              <div class="home-perf__legend">
                <span class="home-perf__legend-item home-perf__legend-item--v1">SHC Team V1 ({{ ytd.v1 }}%)</span>
                <span class="home-perf__legend-item home-perf__legend-item--v3">SHC Team V3 ({{ ytd.v3 }}%)</span>
                <span class="home-perf__legend-item home-perf__legend-item--mkt">{{ t('about.perfMarket') }} (~{{ ytd.market }}%)</span>
              </div>
            </div>

            <div class="home-perf__chart">
              <div class="home-perf__chart-inner">
                <div class="home-perf__chart-yaxis">
                  <span>{{ perfChart.yMax }}%</span>
                  <span>{{ Math.round(perfChart.yMax * 0.67) }}%</span>
                  <span>{{ Math.round(perfChart.yMax * 0.33) }}%</span>
                  <span>0%</span>
                </div>
                <div class="home-perf__chart-area">
                  <svg
                    :viewBox="`0 0 ${perfChart.svgW} ${perfChart.svgH}`"
                    preserveAspectRatio="xMidYMid meet"
                    class="home-perf__svg"
                    aria-hidden="true"
                    @mouseleave="hideTooltip"
                  >
                    <defs>
                      <linearGradient id="hpGradV1" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stop-color="#e8c96a"/>
                        <stop offset="100%" stop-color="#c8a246"/>
                      </linearGradient>
                      <linearGradient id="hpGradV3" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stop-color="#4db87a"/>
                        <stop offset="100%" stop-color="#2f8c56"/>
                      </linearGradient>
                    </defs>

                    <!-- Guide lines -->
                    <line
                      v-for="g in perfChart.guides" :key="g.val"
                      :x1="perfChart.padL" :y1="g.y"
                      :x2="perfChart.svgW - perfChart.padR" :y2="g.y"
                      :stroke="g.val === 0 ? '#c8bfb4' : '#ede8de'"
                      :stroke-width="g.val === 0 ? 1.5 : 1"
                      :stroke-dasharray="g.val === 0 ? '' : '4 3'"
                    />
                    <!-- Y labels -->
                    <text
                      v-for="g in perfChart.guides" :key="'y'+g.val"
                      :x="perfChart.padL - 4" :y="g.y + 4"
                      text-anchor="end" font-size="9" fill="#b3aa9f"
                    >{{ g.val }}%</text>

                    <!-- Bars + labels per group -->
                    <g v-for="(grp, i) in perfChart.groups" :key="i">
                      <!-- V1 bar -->
                      <rect
                        :x="grp.v1X" :y="grp.v1Y"
                        :width="perfChart.barW" :height="grp.v1H"
                        fill="url(#hpGradV1)" rx="3"
                        style="cursor:pointer"
                        @mouseenter="showTooltip($event, grp.label, 'SHC Team V1', grp.v1Val, '#c8a246')"
                      />
                      <text
                        :x="grp.v1X + perfChart.barW / 2" :y="grp.v1LabelY"
                        text-anchor="middle" font-size="8" font-weight="700" fill="#8b6914"
                      >+{{ grp.v1Val }}%</text>

                      <!-- V3 bar -->
                      <rect
                        :x="grp.v3X" :y="grp.v3Y"
                        :width="perfChart.barW" :height="grp.v3H"
                        fill="url(#hpGradV3)" rx="3"
                        style="cursor:pointer"
                        @mouseenter="showTooltip($event, grp.label, 'SHC Team V3', grp.v3Val, '#2f8c56')"
                      />
                      <text
                        :x="grp.v3X + perfChart.barW / 2" :y="grp.v3LabelY"
                        text-anchor="middle" font-size="8" font-weight="700" fill="#1e6b3e"
                      >+{{ grp.v3Val }}%</text>

                      <!-- Market bar (xám, âm thì xuống dưới zero line) -->
                      <rect
                        :x="grp.mktX" :y="grp.mktY"
                        :width="perfChart.barW" :height="grp.mktH"
                        :fill="grp.isNeg ? '#e07070' : '#b3aa9f'"
                        rx="3"
                        style="cursor:pointer"
                        @mouseenter="showTooltip($event, grp.label, 'TB Thị trường', grp.mktVal, grp.isNeg ? '#e07070' : '#9a9088')"
                      />
                      <text
                        :x="grp.mktX + perfChart.barW / 2" :y="grp.mktLabelY"
                        text-anchor="middle" font-size="8" font-weight="700"
                        :fill="grp.isNeg ? '#c0504d' : '#9a9088'"
                      >{{ grp.mktVal }}%</text>

                      <!-- Month label -->
                      <text
                        :x="grp.cx" :y="grp.labelY"
                        text-anchor="middle" font-size="9" fill="#9a9088"
                      >{{ grp.label }}</text>
                    </g>

                    <!-- Bỏ market polyline, đã có cột riêng -->

                  </svg>

                  <!-- Tooltip ngoài SVG để không bị clip -->
                  <div
                    v-if="tooltip"
                    class="hp-tooltip hp-tooltip--floating"
                    :style="{
                      left: `${tooltip.x - 60}px`,
                      top: `${tooltip.y - 52}px`,
                      borderColor: tooltip.color
                    }"
                  >
                    <span class="hp-tooltip__team" :style="{ color: tooltip.color }">{{ tooltip.team }}</span>
                    <span class="hp-tooltip__month">{{ tooltip.label }}</span>
                    <span class="hp-tooltip__val" :style="{ color: tooltip.color }">{{ tooltip.val > 0 ? '+' : '' }}{{ tooltip.val }}%</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="home-perf__stats">
              <div class="home-perf__stat">
                <small>SHC TEAM V1</small>
                <strong class="home-perf__stat--gold">{{ ytd.v1 }}%</strong>
                <span>{{ t('home.perfV1Desc') }}</span>
              </div>
              <div class="home-perf__stat">
                <small>SHC TEAM V3</small>
                <strong class="home-perf__stat--green">{{ ytd.v3 }}%</strong>
                <span>{{ t('home.perfV3Desc') }}</span>
              </div>
              <div class="home-perf__stat">
                <small>{{ t('home.perfBestMonth') }}</small>
                <strong>{{ bestMonth.label }}</strong>
                <span>V1 {{ bestMonth.v1 }}% · V3 {{ bestMonth.v3 }}%</span>
              </div>
              <div class="home-perf__stat">
                <small>{{ t('home.perfMarketLabel') }}</small>
                <strong>~{{ ytd.market }}%</strong>
                <span>{{ t('home.perfMarketDesc') }}</span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>

    <section class="home-cta-final">
      <div class="home-shell home-cta-final__inner">
        <p class="home-cta-final__eyebrow">{{ t('home.ctaFinalEyebrow') }}</p>
        <h2 class="home-cta-final__title">
          <span>{{ t('home.ctaFinalLine1') }}</span>
          <span class="home-cta-final__dash">{{ t('home.ctaFinalDash') }}</span>
          <em>{{ t('home.ctaFinalLine2') }}</em>
        </h2>
        <p class="home-cta-final__desc">{{ t('home.ctaFinalDesc') }}</p>
        <div class="home-cta-final__actions">
          <router-link
            to="/contact"
            class="home-cta-final__btn home-cta-final__btn--primary"
          >
            {{ t('home.ctaFinalBtnPrimary') }}
          </router-link>
          <a
            href="https://t.me/songhancapital"
            target="_blank"
            rel="noopener noreferrer"
            class="home-cta-final__btn home-cta-final__btn--secondary"
          >
            {{ t('home.ctaFinalBtnSecondary') }}
          </a>
        </div>
      </div>
    </section>

  </div>
</template>

<script src="./home.js"></script>
<style src="./home.scss" lang="scss"></style>
